/**
 * One marketing version, one commit (SCH-2563).
 *
 * A marketing version (`apps/mobile/version.json` `version`, e.g. 1.9.1) names
 * exactly one commit for the whole fleet, and its tag `mobile/v<version>` points
 * at that commit. The backend writes the tag on the first successful production
 * release of the version from `main` or a `release/mobile-*` branch (KTD1, KTD3).
 * A release from any other ref ships, but never creates or moves a tag (R3).
 *
 * The ref rules live here rather than in the backend so the dispatch dialogs
 * can predict them from the same code the writer runs.
 *
 * Plan: docs/plans/2026-10-01-2332-feat-fleet-version-tag-identity-plan.md
 */

import { z } from 'zod'

/** Prefix of every version tag. `mobile/v1.9.1` names version 1.9.1. */
export const MOBILE_VERSION_TAG_PREFIX = 'mobile/v'

/** Prefix of the backport branches a production release may be cut from. */
export const MOBILE_RELEASE_BRANCH_PREFIX = 'release/mobile-'

/** The tag a marketing version is recorded under. */
export function mobileVersionTagName(version: string): string {
  return `${MOBILE_VERSION_TAG_PREFIX}${version}`
}

/**
 * Can a production release from this ref establish a version (R2, R3)?
 *
 * `main` and `release/mobile-*` only. A `mobile/v*` tag cannot: it ships a
 * version that is already established, so a release at one never writes. `dev`,
 * feature branches and a hand-started run with no dispatch record never do.
 */
export function refEstablishesVersion(ref: string | null | undefined): boolean {
  if (!ref) return false
  return ref === 'main' || ref.startsWith(MOBILE_RELEASE_BRANCH_PREFIX)
}

/** A full git commit SHA, the only form the writer tags and the callback accepts. */
export const GitCommitShaSchema = z
  .string()
  .regex(/^[0-9a-f]{40}$/i)
  .transform(sha => sha.toLowerCase())

/** Which lane produced the success that established the version. */
export const MobileVersionTagLaneSchema = z.enum(['ota', 'build'])
export type MobileVersionTagLane = z.infer<typeof MobileVersionTagLaneSchema>

/**
 * The release whose commit the tag names: the earliest successful eligible
 * production release of the version (KTD3), not necessarily the release whose
 * success triggered the write.
 */
const EstablishingReleaseSchema = z.object({
  /** `AppRelease.publicId`. */
  releaseId: z.string(),
  /** The ref the release was dispatched from. */
  ref: z.string(),
  lane: MobileVersionTagLaneSchema,
  dispatchId: z.string()
})

/** `version.tagged`: the tag was created at the establishing release's commit. */
export const VersionTaggedEventPayloadSchema = EstablishingReleaseSchema.extend({
  version: z.string(),
  tag: z.string(),
  commit: z.string()
})
export type VersionTaggedEventPayload = z.infer<typeof VersionTaggedEventPayloadSchema>

/**
 * `version.tag_conflict`: the tag already exists at another commit. Nothing is
 * moved; both commits are recorded so staff can decide which one the version
 * names.
 */
export const VersionTagConflictEventPayloadSchema = EstablishingReleaseSchema.extend({
  version: z.string(),
  tag: z.string(),
  /** The establishing release's commit, which the writer would have tagged. */
  commit: z.string(),
  /** The commit the existing tag points at. */
  existingCommit: z.string()
})
export type VersionTagConflictEventPayload = z.infer<typeof VersionTagConflictEventPayloadSchema>

/**
 * `version.tag_failed`: GitHub could not be read or written. The release is
 * unaffected (R4); the next eligible success of the version tries again.
 */
export const VersionTagFailedEventPayloadSchema = z.object({
  version: z.string(),
  tag: z.string(),
  /** The commit the writer meant to tag; null when the failure came before one was chosen. */
  commit: z.string().nullable(),
  /** Which GitHub call failed. */
  stage: z.enum(['read_tag', 'create_tag', 'unexpected']),
  errorType: z.string(),
  status: z.number().int().nullable(),
  message: z.string()
})
export type VersionTagFailedEventPayload = z.infer<typeof VersionTagFailedEventPayloadSchema>
